// providers/ThemeProvider.tsx
//
// Runtime coordinator for the Theme_System. Holds the current preference and the
// effective theme, applies the effective theme to `document.documentElement`,
// tracks OS colour-scheme changes while the preference is `System`, loads the
// stored preference after authentication, and persists changes via the Settings_API.
//
// This provider is strictly presentational (Req 9): it mutates only the
// `data-theme` attribute and the `theme-transition` class on <html>, and calls
// the theme API. It imports no domain/calculation modules.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  normaliseStoredPreference,
  readOsPreference,
  resolveEffectiveTheme,
  type EffectiveTheme,
  type ThemePreference,
} from '../theme/resolveEffectiveTheme';
import { getThemePreference, putThemePreference } from '../theme/themeApi';
import { useAuth } from './AuthProvider';

interface ThemeContextValue {
  preference: ThemePreference; // Light | Dark | System (current selection)
  effectiveTheme: EffectiveTheme; // light | dark (actually applied)
  setPreference: (p: ThemePreference) => void; // apply immediately + persist
  persistError: boolean; // true when the last save failed (Req 7.3)
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

// Slightly longer than the 0.25s CSS transition so the class is removed only
// after the animation has completed (Req 6.3).
const TRANSITION_WINDOW_MS = 260;

/** Seed the effective theme from the theme already applied by the FOUC script. */
function readSeedTheme(): EffectiveTheme {
  if (typeof document === 'undefined') return 'dark';
  return document.documentElement.dataset.theme === 'light' ? 'light' : 'dark';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();

  // INIT (Req 6.1, 6.2): seed from the FOUC-applied theme; preference defaults to System.
  const [preference, setPreferenceState] = useState<ThemePreference>('System');
  const [effectiveTheme, setEffectiveTheme] = useState<EffectiveTheme>(readSeedTheme);
  const [persistError, setPersistError] = useState<boolean>(false);

  // Tracks the theme currently written to <html> so we can detect real changes
  // and only run the transition when the applied theme actually differs (Req 6.3, 6.4).
  const appliedThemeRef = useRef<EffectiveTheme>(readSeedTheme());
  const rafRef = useRef<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clears any pending transition-cleanup raf/timer.
  const clearTransitionTimers = useCallback(() => {
    if (rafRef.current !== null && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(rafRef.current);
    }
    rafRef.current = null;
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // APPLY + TRANSITION (Req 2.2, 3.7, 6.3, 6.4): whenever effectiveTheme changes,
  // write data-theme and — only if the applied theme actually changed — toggle the
  // `theme-transition` class for a single short window to produce exactly one transition.
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const root = document.documentElement;
    const changed = appliedThemeRef.current !== effectiveTheme;

    root.dataset.theme = effectiveTheme;
    appliedThemeRef.current = effectiveTheme;

    if (!changed) {
      // Unchanged theme: no class, no transition (Req 6.4).
      return;
    }

    // Changed theme: run exactly one transition. Reduced motion is handled by the
    // CSS rule (gated on prefers-reduced-motion: no-preference), so simply toggling
    // the class is sufficient — no JS media check needed.
    clearTransitionTimers();
    root.classList.add('theme-transition');

    const removeClass = () => {
      root.classList.remove('theme-transition');
      timerRef.current = null;
    };

    if (typeof requestAnimationFrame !== 'undefined') {
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        timerRef.current = setTimeout(removeClass, TRANSITION_WINDOW_MS);
      });
    } else {
      timerRef.current = setTimeout(removeClass, TRANSITION_WINDOW_MS);
    }
  }, [effectiveTheme, clearTransitionTimers]);

  // Clean up any pending transition timers on unmount.
  useEffect(() => clearTransitionTimers, [clearTransitionTimers]);

  // POST-AUTH LOAD (Req 7.1, 7.2): when authenticated, load the stored preference.
  // On success adopt it; on failure keep System + the OS-derived theme. Either way
  // recompute the effective theme. Guard against setting state after unmount.
  useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;

    (async () => {
      let nextPreference: ThemePreference = 'System';
      try {
        nextPreference = normaliseStoredPreference(await getThemePreference());
      } catch {
        // Swallow the error: keep System and the OS-derived theme (Req 7.1, 7.2).
        nextPreference = 'System';
      }
      if (cancelled) return;
      setPreferenceState(nextPreference);
      setEffectiveTheme(resolveEffectiveTheme(nextPreference, readOsPreference()));
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  // Centralised recompute: whenever `preference` changes, recompute the effective
  // theme from (preference, current OS preference).
  useEffect(() => {
    setEffectiveTheme(resolveEffectiveTheme(preference, readOsPreference()));
  }, [preference]);

  // OS TRACKING (Req 4.4, 5.8): while preference === 'System', react to OS changes.
  // Unsubscribe when the preference is not System and on unmount.
  useEffect(() => {
    if (preference !== 'System') return;
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      setEffectiveTheme(resolveEffectiveTheme('System', readOsPreference()));
    };

    mql.addEventListener('change', handleChange);
    return () => {
      mql.removeEventListener('change', handleChange);
    };
  }, [preference]);

  // setPreference (Req 3.6, 3.7, 5.5, 7.3): reflect immediately, apply within the
  // same interaction (no reload), clear prior error optimistically, then persist.
  const setPreference = useCallback((newPref: ThemePreference) => {
    setPreferenceState(newPref);
    setEffectiveTheme(resolveEffectiveTheme(newPref, readOsPreference()));
    setPersistError(false);

    putThemePreference(newPref).catch(() => {
      // Keep the in-session selection and applied theme; surface a non-blocking error.
      setPersistError(true);
    });
  }, []);

  return (
    <ThemeContext.Provider value={{ preference, effectiveTheme, setPreference, persistError }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
